import { CheckRes, LayoutState, PartData, UploadRes } from "../types/types"
import { getErrors } from "../utils/checkLayoutErrors"
import { createCloseIcon, createCloudIcon, createInfoIcon, createModal, createTimeIcon } from "../utils/createElemets"
import { createLayoutErrorsModal } from "../utils/createLayoutErrorsModal"
import { uploadWithProgress } from "../utils/uploadWithProgress"
import { BASE_API_URL, LayoutsUploadModal } from "../layouts-upload-modal"
import { filterAllowedFiles } from "../utils/allowedFilesFilter"

// --------------------------------------------------------------------------------

export class UploadZone {
    #uploadZone: HTMLDivElement
    #dropZoneContainer: HTMLDivElement
    #label: HTMLLabelElement
    #span: HTMLSpanElement

    #checkProgress: number

    #partData: PartData
    #modal: LayoutsUploadModal
    #index: number
    #uploadState: 'ready' | 'uploading' | 'check' | 'result'

    #readyElements: HTMLElement[]
    #resultElement: HTMLElement

    constructor(partData: PartData, index: number, modal: LayoutsUploadModal) {
        this.#partData = partData
        this.#modal = modal
        this.#index = index
        this.#checkProgress = 0

        this.#uploadZone = document.createElement('div')
        this.#uploadZone.classList.add('upload-zone')
        this.#dropZoneContainer = document.createElement('div')
        this.#dropZoneContainer.classList.add('drop-zone')
        this.#label = document.createElement('label')
        this.#label.classList.add('ready')
        this.#span = document.createElement('span')
        this.#uploadState = 'ready'

        // Ready state elements ----------
        const readyDesktop = document.createElement('div')
        readyDesktop.classList.add('desktop')
        readyDesktop.textContent = 'Перетащите файл или'
        readyDesktop.prepend(createCloudIcon())

        const readyMobile = document.createElement('div')
        readyMobile.classList.add('mobile')
        readyMobile.textContent = 'Загрузить файл'

        const allowedFilesString = window.layoutExtensions
            ? '.' + window.layoutExtensions?.reduce((acc, value) => acc + ', .' + value) : ''

        const input = document.createElement('input')
        input.type = 'file'
        if (window.layoutExtensions)
            input.accept = allowedFilesString
        input.onchange = (e: Event) => {
            const inputTarget = e.target as HTMLInputElement
            if (!inputTarget.files) return

            const filteredFiles = filterAllowedFiles(Array.from(inputTarget.files))
            if (filteredFiles.length === 0) {
                createModal({
                    title: 'Файл не поддерживается',
                    subTitle: 'Только: ' + allowedFilesString,
                    submitText: 'Ok'
                }, this.#modal.shadowRoot)
                return
            }

            const file = inputTarget.files?.item(0)
            this.#uploadFile(file)

            inputTarget.files = null
            inputTarget.value = ''
        }

        const readyDndLayer = document.createElement('div')
        readyDndLayer.classList.add('dnd-layer')
        readyDndLayer.ondragenter = e => this.#label.classList.add('hovered')
        readyDndLayer.ondragleave = e => this.#label.classList.remove('hovered')
        readyDndLayer.ondrop = (e: DragEvent) => {
            e.preventDefault()
            if (!e.dataTransfer) return

            const dataList: File[] = []

            if (e.dataTransfer.items)
                Array.from(e.dataTransfer.items).forEach((item, i) => {
                    if (item.kind === "file") {
                        const file = item.getAsFile()
                        file && dataList.push(file)
                    }
                })
            else
                Array.from(e.dataTransfer.files).forEach((file, i) => {
                    dataList.push(file)
                })


            const filteredFiles = filterAllowedFiles(dataList)
            if (filteredFiles.length === 0) {
                createModal({
                    title: 'Файл не поддерживается',
                    subTitle: 'Только: ' + allowedFilesString,
                    submitText: 'Ok'
                }, this.#modal.shadowRoot)
                return
            }

            this.#uploadFile(dataList[0])
            e.dataTransfer.clearData()
        }

        this.#readyElements = [readyDesktop, readyMobile, input, readyDndLayer]

        this.#span.textContent = 'Выберите макет с компьютера'
        this.#label.append(...this.#readyElements, this.#span)

        // Result state elements ----------
        this.#resultElement = document.createElement('div')
        this.#resultElement.classList.add('file')

        this.#uploadZone.append(this.#dropZoneContainer)

        this.#dropZoneContainer.append(this.#label)

        if (this.#partData && this.#partData.countPrintingSides === 2) {
            setTimeout(() => {
                const span = document.createElement('span')
                span.classList.add('subtitle')
                span.textContent = index === 0 ? 'Лицевая' : 'Оборотная'
                this.#dropZoneContainer.parentElement?.prepend(span)
            })
        }

        if (this.#partData && layoutsState[this.#partData.id][this.#index]) {
            this.#state = 'result'
            this.#layoutState = layoutsState[this.#partData.id][this.#index]
        }

    }

    async #timeOut() {
        let timeout = 2000
        if (window.enfocus && window.pitStop.interval)
            timeout = window.pitStop.interval * 1000

        return new Promise((resolve, reject) => {
            setTimeout(() => resolve(true), timeout)
        })
    }

    async #uploadFile(file: File | undefined | null) {
        const calcModule = document.querySelector('form.calculation-module') as HTMLFormElement | null
        const dataProduct = calcModule?.dataset.product ?? ''

        if (file && window.partsData) {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('id', dataProduct) // При отрытии на странице с продуктом, нужно передавать id продукта
            formData.append('positionId', window.positionId ?? '') // Когда открываем в корзине
            formData.append('partId', this.#partData?.id ?? 'undefined')
            formData.append('partsData', JSON.stringify(window.partsData))
            formData.append('selectedParams', window.selectedParams ? JSON.stringify(window.selectedParams) : 'undefined')
            formData.append('species', this.#partData?.numberSpecies ?? 'undefined')

            this.#state = 'uploading'
            try {
                const uploadResData = await uploadWithProgress({
                    url: BASE_API_URL + window.uploadLayoutUrl,
                    data: formData,
                    container: this.#label,
                    span: this.#span
                }) as UploadRes

                let checkResData: CheckRes | undefined = undefined

                if (uploadResData.withCheck === 1) {
                    this.#state = 'check'
                    let attemts = 0

                    while (attemts < 100) {
                        const checkRes = await fetch(BASE_API_URL + uploadResData.checkUrl, {
                            signal: window.enfocus && window.pitStop.waiting_limit
                                ? AbortSignal.timeout(window.pitStop.waiting_limit * 1000)
                                : undefined
                        })
                        checkResData = await checkRes.json() as CheckRes

                        if (checkResData.success === -1)
                            throw new Error(checkResData.message)

                        if (checkResData.success === false)
                            throw new Error(checkResData.message)

                        if (checkResData.progress > 0)
                            this.#checkProgress = checkResData.progress

                        this.#span.textContent = `${this.#checkProgress} %`
                        this.#label.style.backgroundPosition = `${100 - this.#checkProgress}%`
                        this.#label.style.transitionDuration = '0.14s'

                        if (checkResData.success === 1) break

                        attemts++
                        await this.#timeOut()
                    }

                    this.#label.removeAttribute('style')
                    this.#checkProgress = 0
                }

                this.#state = 'result'

                if (this.#partData && this.#index !== undefined) {

                    if (checkResData && Object.keys(checkResData.result).length === 2 && this.#index === 0) {
                        const second = this.#modal.uploadZones[1]
                        this.#modal.twoSidePdf = true

                        const [firstResult, secondResult] =
                            Object.entries(checkResData.result)
                                .map(([key, value]) => ({
                                    message: checkResData.message,
                                    progress: checkResData.progress,
                                    result: { [key]: value },
                                    success: checkResData.success
                                } as CheckRes))


                        let temp = layoutsState[this.#partData.id].slice()
                        temp[0] = {
                            partData: this.#partData,
                            uploadResData: { ...uploadResData, index: Object.values(firstResult.result)[0].index },
                            checkResData: firstResult,
                            ...getErrors(firstResult),
                            index: 0
                        }
                        temp[1] = {
                            partData: second.#partData,
                            uploadResData: { ...uploadResData, index: Object.values(secondResult.result)[0].index },
                            checkResData: secondResult,
                            ...getErrors(secondResult),
                            index: 1
                        }
                        this.#layoutState = temp[0]

                        second.#state = 'result'
                        second.#layoutState = null
                        second.#layoutState = temp[1]

                        layoutsState[this.#partData.id] = temp

                    } else {
                        let temp = layoutsState[this.#partData.id].slice()
                        temp[this.#index] = { partData: this.#partData, uploadResData, checkResData, ...getErrors(checkResData), index: this.#index }
                        this.#layoutState = temp[this.#index]

                        layoutsState[this.#partData.id] = temp
                    }
                }

            } catch (e) {
                const err = e as Error
                this.#state = 'ready'
                this.#span.innerHTML = 'Выберите макет с компьютера'
                this.#label.removeAttribute('style')
                this.#checkProgress = 0

                createModal({
                    title: "Ошибка сервера",
                    subTitle: 'message' in err && err.message !== 'signal timed out'
                        ? err.message
                        : "Пожалуйста, попробуйте позже. Мы уже решаем проблему",
                    submitText: "Ok"
                }, this.#uploadZone)
            }
        }
    }

    clearState() {
        this.#state = 'ready'
        let temp = layoutsState[this.#partData.id].slice()
        temp[this.#index] = null
        this.#layoutState = temp[this.#index]

        layoutsState[this.#partData.id] = temp
    }

    set #layoutState(layoutState: LayoutState | null) {
        if (layoutState) {
            const { errors, warnings } = getErrors(layoutState.checkResData)

            this.#resultElement.classList.add(errors ? 'error' : warnings ? 'warning' : 'ok')
            const name = document.createElement('span')
            name.textContent = layoutState.uploadResData.original_name

            const closeBtn = document.createElement('button')
            closeBtn.append(createCloseIcon('var(--gray-500, #A0AEC0)'))
            closeBtn.onclick = () => createModal({
                title: 'Вы уверены что хотите отменить загрузку файлов?',
                subTitle: 'Все загруженные макеты будут утрачены',
                submitText: 'Да, отменить',
                cancelText: 'Не отменять',
                action: () => {
                    if (!this.#partData) return
                    if (this.#modal.twoSidePdf) {
                        this.#modal.uploadZones[0].clearState()
                        this.#modal.uploadZones[1].clearState()

                        this.#modal.twoSidePdf = false
                    } else
                        this.clearState()
                }
            }, this.#uploadZone)

            const iconStatus = errors ? 'error' : warnings ? 'warning' : 'ok'
            this.#resultElement.append(createInfoIcon(iconStatus), name, closeBtn)

            if (errors || warnings) {
                const errorLink = document.createElement('div')
                errorLink.classList.add('error-link')
                errorLink.textContent = errors ? 'Смотреть критичные замечания' : 'Смотреть замечания'
                errorLink.onclick = () => {
                    if (!this.#partData || !this.#modal.shadowRoot) return
                    createLayoutErrorsModal(layoutState, this.#index, this.#modal.shadowRoot, undefined, this)
                }
                this.#dropZoneContainer.append(errorLink)
            }
        } else {
            this.#resultElement.innerHTML = ''
            this.#resultElement.setAttribute('class', 'file')
        }
    }

    get #state() {
        return this.#uploadState
    }

    set #state(state) {
        this.#uploadState = state
        this.#label.setAttribute('class', state)

        const span = document.createElement('span')
        span.classList.add('suffix')

        switch (state) {
            case 'ready':
                this.#span.textContent = 'Выберите макет с компьютера'
                this.#label.replaceChildren(...this.#readyElements, this.#span)
                this.#dropZoneContainer.replaceChildren(this.#label)
                break

            case 'uploading':
                this.#label.innerHTML = 'Идет загрузка макета'
                this.#label.prepend(createTimeIcon())
                span.textContent = 'KB'
                this.#label.append(this.#span, span)
                break

            case 'check':
                this.#label.innerHTML = 'Идет проверка макета'
                this.#label.prepend(createTimeIcon())
                this.#label.append(this.#span)
                break

            case 'result':
                this.#dropZoneContainer.replaceChildren(this.#resultElement)
                break

            default:
                break
        }
    }

    get view() {
        return this.#uploadZone
    }
}